import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { credentialsApi } from "../../api/credentials.api.js";
import { unwrapError } from "../../api/client.js";
import { ActionButton, GhostButton, Icon, MiniSpinner, Modal, colors, inputStyle, } from "../ui/index.js";
import { useToast } from "../ui/ToastProvider.js";
export function AddCredentialModal({ onClose }) {
    const qc = useQueryClient();
    const { showToast } = useToast();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [probe, setProbe] = useState(null);
    const probeM = useMutation({
        mutationFn: () => credentialsApi.probe({ username: username.trim(), password }),
        onSuccess: (result) => {
            setProbe({
                type: "ok",
                memberId: result.memberId,
                venues: result.accessibleVenues.length,
                credits: result.creditsRemaining,
            });
            setError(null);
        },
        onError: async (err) => {
            const apiErr = await unwrapError(err);
            setProbe({ type: "error", message: apiErr.message });
        },
    });
    const add = useMutation({
        mutationFn: () => credentialsApi.add({
            label: username.trim(),
            username: username.trim(),
            password,
        }),
        onSuccess: () => {
            showToast("Credential added", "success");
            void qc.invalidateQueries({ queryKey: ["credentials"] });
            onClose();
        },
        onError: async (err) => {
            const apiErr = await unwrapError(err);
            setError(apiErr.message);
        },
    });
    const busy = add.isPending || probeM.isPending;
    const handleSubmit = (e) => {
        e.preventDefault();
        setError(null);
        if (!username.trim() || !password) {
            setError("Email and password are required.");
            return;
        }
        add.mutate();
    };
    const handleTest = () => {
        setError(null);
        setProbe(null);
        if (!username.trim() || !password) {
            setError("Email and password are required to test.");
            return;
        }
        probeM.mutate();
    };
    return (_jsxs(Modal, { onClose: onClose, title: "Add credential", width: 420, children: [_jsx("p", { style: {
                    color: colors.textSecondary,
                    fontSize: "0.85rem",
                    marginTop: 0,
                    marginBottom: 20,
                }, children: "We will validate the login immediately and store the password securely." }), _jsxs("form", { onSubmit: handleSubmit, children: [_jsx(Field, { label: "Email", id: "cred-username", value: username, onChange: setUsername, type: "email", disabled: busy }), _jsx(Field, { label: "Password", id: "cred-password", value: password, onChange: setPassword, type: "password", disabled: busy }), probe?.type === "ok" && (_jsxs("div", { role: "status", style: {
                            background: colors.successLight,
                            color: colors.success,
                            padding: 10,
                            borderRadius: 6,
                            fontSize: "0.85rem",
                            marginBottom: 12,
                        }, children: [_jsx(Icon, { name: "check", size: 14 }), " Login works \u2014 ", probe.credits, " credit", probe.credits === 1 ? "" : "s", ", access to ", probe.venues, " venue", probe.venues === 1 ? "" : "s", "."] })), probe?.type === "error" && (_jsxs("div", { role: "alert", style: {
                            background: colors.dangerLight,
                            color: colors.danger,
                            padding: 10,
                            borderRadius: 6,
                            fontSize: "0.85rem",
                            marginBottom: 12,
                        }, children: [_jsx(Icon, { name: "x", size: 14 }), " ", probe.message] })), error && !probe && (_jsx("div", { role: "alert", style: {
                            background: colors.dangerLight,
                            color: colors.danger,
                            padding: 10,
                            borderRadius: 6,
                            fontSize: "0.85rem",
                            marginBottom: 12,
                        }, children: error })), _jsxs("div", { style: {
                            display: "flex",
                            gap: 8,
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: 16,
                        }, children: [_jsx(GhostButton, { type: "button", onClick: handleTest, disabled: busy, children: probeM.isPending ? (_jsxs(_Fragment, { children: [_jsx(MiniSpinner, { size: 12 }), " Testing"] })) : ("Test login") }), _jsxs("div", { style: { display: "flex", gap: 8 }, children: [_jsx(GhostButton, { onClick: onClose, disabled: busy, children: "Cancel" }), _jsx(ActionButton, { type: "submit", disabled: busy, children: add.isPending ? (_jsxs(_Fragment, { children: [_jsx(MiniSpinner, { size: 12, color: colors.surface }), " Saving"] })) : ("Save credential") })] })] })] })] }));
}
function Field({ id, label, value, onChange, type = "text", placeholder, disabled }) {
    return (_jsxs("label", { htmlFor: id, style: { display: "block", marginBottom: 12 }, children: [_jsx("span", { style: {
                    display: "block",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    marginBottom: 4,
                    color: colors.text,
                }, children: label }), _jsx("input", { id: id, type: type, value: value, placeholder: placeholder, disabled: disabled, onChange: (e) => onChange(e.target.value), autoComplete: type === "password" ? "new-password" : type === "email" ? "username" : "off", style: { ...inputStyle, width: "100%", color: colors.text } })] }));
}
